/**
 * Merchant Management Service
 * API functions for merchant management operations
 */
import { commonServerInstance } from '@/utilities/instances'

// Type definitions
export interface IMerchant {
  id: string
  company_name: string
  status: 'Active' | 'Inactive'
  email: string
  address: string
  billing_period: string
  type: string
  term: string
  last_billing_time: string
  created_at: string
  nickname: string
}

export interface IPartner {
  id: string
  name: string
  customLabel?: string
}

export interface IAccount {
  id: string
  username: string
}

export interface IMerchantDetail {
  parent: {
    address: string
    adr_line_1: string
    adr_line_2: string
    billing_period: string
    city: string
    contact_name: string
    country: string
    created_at: string
    currency: string
    email: string
    last_billing_time: string
    parent_merchant_formal_name: string
    parent_merchant_id: string
    parent_merchant_nickname: string
    phone: string
    state_province: string
    status: string
    term: number
    team: string
    type: string
    updated_at: string
    zip_postal: string
    team_id: string | number
    bef_auto_submit: boolean
    bef_auto_submit_updated_at: string | null
  }
  customer: {
    data: Array<{
      id: string
      name: string
    }>
    total: number
  }
  authorized: Record<string, string>
}

export interface IMerchantSearchParams {
  partnerId?: string
  status?: string
  page?: number
  per_page?: number
}

// Mock merchant details data is no longer used - replaced with API calls

/**
 * Fetch partners for AutoComplete options
 */
export const fetchMerchantSearchResult = async (params?: {
  page?: number
  per_page?: number
  id?: string
  status?: string
}) => {
  const response = await commonServerInstance.get('/settle/merchant', {
    params
  })
  return response?.data?.data
}

export const toggleMerchantStatus = async (params: {
  partner_id: number
  status: string
}) => {
  const response = await commonServerInstance.post(
    `/settle/merchant/change-status`,
    params
  )
  return response?.data?.data
}

/**
 * Fetch detailed merchant information including base info, customers, and KAs
 */
export const fetchMerchantDetail = async (params: {
  id: string
  page: number
  per_page: number
}): Promise<IMerchantDetail> => {
  const response = await commonServerInstance.get(`/settle/merchant/detail`, {
    params
  })
  return response?.data?.data
}

/**
 * Update merchant base information
 */
export const updateMerchantInfo = async (
  data: Partial<IMerchantDetail['parent']> & {
    partner_id: string
    nickname: string
    company_name: string
  }
) => {
  const response = await commonServerInstance.post(
    `/settle/merchant/edit-merchant`,
    data
  )
  return response?.data?.data
}

export const assignAccountToMerchant = async (data: {
  partner_id: string
  auth_ids: string
  team_id: string | number
}) => {
  const response = await commonServerInstance.post(
    `/settle/merchant/assign-users`,
    data
  )
  return response?.data?.data
}

export const fetchAvailablePartners = async () => {
  const response = await commonServerInstance.get(
    '/settle/merchant/team-partners'
  )
  return response?.data?.data
}

export const fetchTeamMembers = async (params: { id: string | number }) => {
  const response = await commonServerInstance.get('/settle/team/team-members', {
    params
  })
  return response?.data?.data
}

export const fetchTeamList = async () => {
  const response = await commonServerInstance.get('/settle/team')
  return response?.data?.data
}

/**
 * Parameters for the merchant export endpoint (STLDEV-1456).
 * - select_all=true : export everything in the current filter range (intersected
 *   with the caller's scope); `ids` is omitted.
 * - select_all=false: export exactly the checked `ids`.
 * `id`/`status` mirror the current list filters (carried through in select-all mode).
 */
export interface IMerchantExportParams {
  select_all: boolean
  ids?: number[]
  id?: string
  status?: string
}

/**
 * Export merchants. The backend uploads the .xlsx to S3 and returns a
 * short-lived presigned URL; we trigger the browser download from it.
 */
export const exportMerchants = async (params: IMerchantExportParams) => {
  const response = await commonServerInstance.post('/settle/merchant/export', {
    ...params
  })
  const url = response?.data?.data?.url
  if (url) {
    // Presigned S3 URL (cross-origin) — trigger the browser download. The
    // downloaded filename comes from the S3 object key's basename.
    const link = document.createElement('a')
    link.href = url
    document.body.appendChild(link)
    link.click()
    link.remove()
  }
  return response?.data?.data
}
